import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Award, CheckCircle, ArrowRight,
  GraduationCap, FileText,
  Mail, TrendingUp
} from 'lucide-react';
import {
  atAGlance, whoIsFor, entryRequirements, whyChooseFeatures,
  learningOutcomes, courses, careerPathways, admissionsSteps, faqs
} from '../data/mastersPPMProgram';
import { AtAGlanceTable } from '../components/program/AtAGlanceTable';
import { EntryRequirementsSection } from '../components/program/EntryRequirementsSection';
import { CurriculumAccordion } from '../components/program/CurriculumAccordion';
import { FAQAccordion } from '../components/program/FAQAccordion';
import { AdmissionsTimeline } from '../components/program/AdmissionsTimeline';

const MastersPPMProgram = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="masters-ppm-program-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-[#163A59] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #fff 0%, transparent 50%), radial-gradient(circle at 80% 70%, #fff 0%, transparent 50%)' }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#C82032]" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6">
              <Link to="/programs" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium transition-colors">
                <ArrowRight size={14} className="rotate-180" />
                Back to Programs
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 border border-white/25 text-white/90 px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider mb-6">
              <GraduationCap size={13} />
              Master's Degree
            </div>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1]">
              Master of Development Project & Program Management
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-10 max-w-3xl">
              A focused, professionally oriented online program designed for learners seeking practical
              skills in development project management, program coordination, MEAL, project finance,
              donor compliance, and evidence-informed decision-making.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15 border border-white/15 mb-10">
              <div className="bg-[#163A59] px-5 py-4">
                <p className="text-white/60 text-[0.7rem] uppercase tracking-widest mb-1">Duration</p>
                <p className="text-white font-semibold text-base">9 Months</p>
              </div>
              <div className="bg-[#163A59] px-5 py-4">
                <p className="text-white/60 text-[0.7rem] uppercase tracking-widest mb-1">Delivery</p>
                <p className="text-white font-semibold text-base">100% Online</p>
              </div>
              <div className="bg-[#163A59] px-5 py-4">
                <p className="text-white/60 text-[0.7rem] uppercase tracking-widest mb-1">Full Fee</p>
                <p className="text-white font-semibold text-base">USD 3,500</p>
              </div>
              <div className="bg-[#163A59] px-5 py-4">
                <p className="text-white/60 text-[0.7rem] uppercase tracking-widest mb-1">Scholarship</p>
                <p className="text-[#FCA5A5] font-semibold text-sm">90% for Afro-Asia</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/admissions"
                className="inline-flex items-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-3.5 text-sm font-semibold tracking-wide transition-all"
                data-testid="hero-apply-btn"
              >
                Apply Now
                <ArrowRight size={16} />
              </Link>
              <a
                href="mailto:admissions@gihds.org"
                className="inline-flex items-center gap-2 bg-transparent border border-white/40 text-white hover:bg-white hover:text-[#163A59] px-8 py-3.5 text-sm font-semibold tracking-wide transition-all"
                data-testid="hero-contact-btn"
              >
                <Mail size={16} />
                Contact Admissions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Program Overview
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Building Practical Skills for Development Practice
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">
                  The <strong>Master of Development Project & Program Management</strong> is a professionally
                  oriented postgraduate program designed for individuals seeking to strengthen their ability
                  to plan, manage, monitor, finance, and lead development projects and programs across NGO,
                  public sector, donor-funded, and social impact settings.
                </p>
                <p className="mb-6">
                  The program combines applied project management, program strategy, monitoring, evaluation,
                  accountability and learning, project finance, and Theory of Change. It supports learners
                  in building the practical knowledge and professional judgment required to design stronger
                  interventions, manage implementation, track results, use resources responsibly, and make
                  evidence-informed decisions in complex development environments.
                </p>
                <p>
                  This program is especially relevant for professionals and emerging practitioners who want
                  a structured, practical, and career-focused learning pathway in development project and
                  program management.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AtAGlanceTable items={atAGlance} />

      {/* Who This Program Is For */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Target Audience
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Who This Program Is For
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                This program is designed for learners seeking practical development-sector skills and
                stronger readiness for project, program, MEAL, finance, grants, and coordination roles.
              </p>
              <p className="text-slate-600 font-medium mb-4">The program is suitable for:</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-[#F1F5F8] p-8"
            >
              <div className="space-y-3">
                {whoIsFor.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <EntryRequirementsSection requirements={entryRequirements} />

      {/* Why This Program */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Program Benefits
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Why Choose This Program
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              The program connects project management, program strategy, MEAL, development finance,
              and Theory of Change into one focused 9-month learning pathway. It is built for learners
              who want to understand how development projects and programs are designed, financed,
              implemented, monitored, reported, and improved.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#DCE4EA] p-8 hover:border-[#163A59]/30 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-[#163A59]/10 text-[#163A59] flex items-center justify-center rounded-sm mb-6">
                  <feature.icon size={24} />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Outcomes */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                What You'll Achieve
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Learning Outcomes
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Graduates will develop practical knowledge and professional capacity for effective
                development project and program management.
              </p>
              <p className="text-slate-600 font-medium">
                Upon successful completion, learners should be able to:
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {learningOutcomes.map((outcome) => (
                <div key={outcome} className="flex items-start gap-4 bg-white p-4 border border-[#DCE4EA]">
                  <CheckCircle className="w-5 h-5 text-[#163A59] mt-0.5 flex-shrink-0" />
                  <p className="text-slate-600 text-sm">{outcome}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <CurriculumAccordion
        courses={courses}
        description="The program is structured over <strong>9 months</strong> and includes <strong>five taught courses</strong> followed by a <strong>four-month capstone project</strong>. The curriculum builds practical knowledge progressively, moving from development project management to applied capstone practice."
      />

      {/* Certificate & Transcript */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <FileText className="w-12 h-12 text-[#C82032] mb-6" />
              <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
                Credentials
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold mb-6">
                Certificate & Transcript
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Upon successful completion of the program and fulfilment of all academic requirements
                as determined by GIHDS, eligible graduates may be issued institutional completion
                documents by the Institute.
              </p>
              <p className="text-slate-400 text-sm">
                These documents are issued solely by GIHDS and serve as the Institute's formal record
                of completion and academic performance within this program.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-4 mb-3">
                  <Award className="w-6 h-6 text-[#C82032]" />
                  <h3 className="font-semibold text-white">Master's Degree Certificate</h3>
                </div>
                <p className="text-slate-400 text-sm">Official institutional certificate for the program</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-4 mb-3">
                  <FileText className="w-6 h-6 text-[#C82032]" />
                  <h3 className="font-semibold text-white">Academic Transcript</h3>
                </div>
                <p className="text-slate-400 text-sm">Recording completed courses and academic performance</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/10">
                <div className="flex items-center gap-4 mb-3">
                  <Mail className="w-6 h-6 text-[#C82032]" />
                  <h3 className="font-semibold text-white">Letter of Recommendation</h3>
                </div>
                <p className="text-slate-400 text-sm">Upon request and at the Institute's discretion</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Pathways */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Professional Opportunities
            </p>
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Career Pathways
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              The program supports career progression by building practical knowledge in project management,
              program coordination, MEAL, finance, accountability, reporting, and development planning.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {Object.values(careerPathways).map((pathway, index) => (
              <motion.div
                key={pathway.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#F1F5F8] p-8"
              >
                <pathway.icon className="w-10 h-10 text-[#163A59] mb-4" />
                <h3 className="font-semibold text-lg text-slate-900 mb-4">{pathway.title}</h3>
                <ul className="space-y-2">
                  {pathway.roles.map((role) => (
                    <li key={role} className="flex items-center gap-2 text-slate-600 text-sm">
                      <span className="w-1.5 h-1.5 bg-[#C82032] rounded-full"></span>
                      {role}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-8 max-w-2xl mx-auto">
            Career outcomes depend on each learner's prior education, experience, professional context,
            and labour market conditions. The program does not guarantee employment but is designed to
            strengthen professional readiness and sector-relevant capacity.
          </p>
        </div>
      </section>

      {/* Tuition & Scholarship */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <p className="text-sm font-medium uppercase tracking-widest opacity-80 mb-4">
                Investment
              </p>
              <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Tuition & Scholarship
              </h2>
              <p className="text-lg opacity-90 leading-relaxed mb-8">
                Through the <strong>GIHDS Global Scholarship Initiative</strong>, eligible applicants from
                <strong> Africa and Asia</strong> who are committed to development, NGO, public sector, or
                social impact work may receive a <strong> 90% tuition scholarship</strong>.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/admissions"
                  className="inline-flex items-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 rounded-sm font-medium transition-all"
                  data-testid="tuition-apply-btn"
                >
                  Apply Now
                  <ArrowRight size={18} />
                </Link>
                <Link
                  to="/scholarship"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-sm font-medium transition-all"
                >
                  Learn About Scholarships
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-sm overflow-hidden">
                <div className="p-6 border-b border-[#DCE4EA]">
                  <h3 className="font-semibold text-slate-900 mb-4">Tuition</h3>
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b border-[#DCE4EA]">
                        <td className="py-3 text-slate-600">Full Program Fee</td>
                        <td className="py-3 text-right font-bold text-[#163A59] text-xl">USD 3,500</td>
                      </tr>
                      <tr>
                        <td className="py-3 text-slate-600">Scholarship</td>
                        <td className="py-3 text-right font-semibold text-[#C82032]">90% for Afro-Asia students</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AdmissionsTimeline steps={admissionsSteps} />

      <FAQAccordion faqs={faqs} />

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#163A59] to-[#102A43]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <TrendingUp className="w-12 h-12 text-[#C82032] mx-auto mb-6" />
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Take the Next Step Toward<br />Development Leadership
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Advance your knowledge and prepare for meaningful roles in development project and
              program management through the <strong>Master of Development Project & Program Management</strong>.
            </p>
            <p className="text-slate-400 mb-10">
              Eligible applicants from Africa and Asia may benefit from the <strong className="text-white">GIHDS Global Scholarship Initiative</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 rounded-sm font-medium transition-all"
                data-testid="final-apply-btn"
              >
                Apply Now
                <ArrowRight size={18} />
              </Link>
              <a
                href="mailto:admissions@gihds.org"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-10 py-4 rounded-sm font-medium transition-all"
              >
                <Mail size={18} />
                Contact Admissions
              </a>
            </div>

            <div className="text-slate-400 text-sm">
              <p className="mb-2">
                <strong className="text-white">Contact Admissions:</strong>{' '}
                <a href="mailto:admissions@gihds.org" className="text-[#C82032] hover:underline">
                  admissions@gihds.org
                </a>
              </p>
              <p>
                <strong className="text-white">Website:</strong>{' '}
                <a href="https://www.gihds.org" target="_blank" rel="noopener noreferrer" className="text-[#C82032] hover:underline">
                  www.gihds.org
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MastersPPMProgram;
